import { useNuxtApp } from '#app'
import type { CookieStore } from '../../utils/cookieStore'

interface ActivePromotionData {
  promotion_id: string
  promotion_name: string
  creative_name: string
  creative_slot: string
  creative_name_storyblok: string
}

type ActivePromotionCookie = ActivePromotionData & {
  expiresAt: number
}

const COOKIE_KEY = 'active-promotion'
const TTL_MINUTES = 30

/**
 * Active promotion context stored in a short-lived cookie.
 *
 * Usage:
 * - setActivePromotion(...) on banner/product click (last-touch wins)
 * - getActivePromotion() on PLP/PDP or elsewhere to attribute products
 * - clearActivePromotion() on purchase or after first qualifying attribution
 */
const useActivePromotion = () => {
  const { $cookies } = useNuxtApp() as unknown as {
    $cookies: CookieStore
  }

  const getActivePromotion = (): ActivePromotionData | null => {
    try {
      const raw = $cookies.get<ActivePromotionCookie>(COOKIE_KEY)
      if (!raw) return null

      const { expiresAt, ...data }: ActivePromotionCookie =
        typeof raw === 'string' ? JSON.parse(raw) : raw

      if (!expiresAt || expiresAt <= Date.now()) {
        clearActivePromotion()
        return null
      }

      return data
    } catch {
      clearActivePromotion()
      return null
    }
  }

  const setActivePromotion = (data: ActivePromotionData) => {
    const expiresAt = Date.now() + TTL_MINUTES * 60 * 1000
    const payload: ActivePromotionCookie = { ...data, expiresAt }
    $cookies.set(COOKIE_KEY, payload, {
      path: '/',
      maxAge: TTL_MINUTES * 60,
      sameSite: 'lax',
    })
  }

  const clearActivePromotion = () => {
    $cookies.remove(COOKIE_KEY, { path: '/' })
  }

  return {
    getActivePromotion,
    setActivePromotion,
    clearActivePromotion,
  }
}

export default useActivePromotion
